import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, Mail, CheckCircle2 } from 'lucide-react';

const LandingWaitlist = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t('waitlist.invalidEmail'));
      return;
    }
    setSubmitting(true);
    const { error } = await (supabase as any).from('beta_testers').insert({
      email: trimmed,
      name: name.trim() || null,
      status: 'waitlisted',
      source: 'landing_page',
    });
    setSubmitting(false);
    if (error) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('duplicate') || msg.includes('unique')) {
        toast.error(t('waitlist.duplicate'));
        setDone(true);
        return;
      }
      toast.error(t('waitlist.error'));
      return;
    }
    toast.success(t('waitlist.successToast'));
    setDone(true);
  };

  return (
    <section className="py-20 sm:py-28 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        <div className="relative rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background/60 to-background/40 backdrop-blur-xl p-8 sm:p-12 shadow-2xl shadow-primary/10">
          <div className="absolute -top-4 start-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground shadow-lg">
            <Sparkles className="h-3.5 w-3.5" />
            {t('waitlist.badge')}
          </div>

          <div className="text-center space-y-3 mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              {t('waitlist.headline')}
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-lg mx-auto leading-relaxed">
              {t('waitlist.description')}
            </p>
          </div>

          {done ? (
            <div className="flex flex-col items-center text-center gap-3 py-6">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-primary" />
              </div>
              <p className="text-lg font-semibold">{t('waitlist.successTitle')}</p>
              <p className="text-sm text-muted-foreground max-w-xs">
                {t('waitlist.successDesc')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 max-w-md mx-auto">
              <Input
                type="text"
                placeholder={t('waitlist.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl text-base bg-background/60 border-border/60"
              />
              <div className="relative">
                <Mail className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground ${isRTL ? 'right-4' : 'left-4'}`} />
                <Input
                  type="email"
                  inputDir="ltr"
                  required
                  placeholder={t('waitlist.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`h-12 rounded-xl text-base bg-background/60 border-border/60 ${isRTL ? 'pr-11' : 'pl-11'}`}
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl text-base font-semibold shadow-lg shadow-primary/25"
              >
                {submitting ? t('waitlist.submitting') : t('waitlist.cta')}
              </Button>
              <p className="text-[11px] text-center text-muted-foreground/70">
                {t('waitlist.privacy')}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default LandingWaitlist;
